import { wrapClassNames } from '@ui/utils/tailwind';
import type React from 'react';
import { forwardRef } from 'react';

export const inputSizes = {
  md: 'px-3 h-10 typo-subhead',
  lg: 'px-4 h-13 typo-headline',
};

export type InputSize = keyof typeof inputSizes;

export const inputVariants = {
  default: 'border-border focus:border-blue-600 focus:ring-blue-600 hover:border-neutral-300',
  error:
    'border-status-danger-bg focus:border-status-danger-bg focus:ring-status-danger-bg hover:border-status-danger-bg',
  success:
    'border-status-success-bg focus:border-status-success-bg focus:ring-status-success-bg hover:border-status-success-bg',
};

export type InputVariant = keyof typeof inputVariants;

export const inputShapes = {
  default: { md: 'rounded-lg', lg: 'rounded-xl' },
  pill: 'rounded-full',
};

export type InputShape = keyof typeof inputShapes;

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  htmlSize?: React.InputHTMLAttributes<HTMLInputElement>['size'];
  shape?: InputShape;
  size?: InputSize;
  variant?: InputVariant;
  tw?: never;
}

export const inputStyles = `
  text-typography border bg-white transition-colors
  disabled:bg-background-secondary disabled:text-typography-secondary
  focus:ring-1
  placeholder:text-typography/50
`;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ htmlSize, shape = 'default', size = 'md', variant = 'default', className = '', ...rest }, ref) => {
    return (
      <input
        className={wrapClassNames(`
          font-medium
          ${inputStyles}
          ${inputSizes[size]}
          ${inputVariants[variant]}
          ${shape === 'pill' ? inputShapes.pill : inputShapes[shape][size]}
          ${className}
        `)}
        ref={ref}
        size={htmlSize}
        {...rest}
      />
    );
  }
);

const textareaSizes = {
  md: 'p-3 typo-subhead',
  lg: 'p-4 typo-headline',
};

type TextareaSize = keyof typeof textareaSizes;

const textareaVariants = {
  default: inputVariants['default'],
  error: inputVariants['error'],
  success: inputVariants['success'],
};

type TextareaVariant = keyof typeof textareaVariants;

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  shape?: InputShape;
  size?: TextareaSize;
  variant?: TextareaVariant;
  tw?: never;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ shape = 'default', size = 'md', variant = 'default', className = '', ...rest }, ref) => {
    return (
      <textarea
        className={wrapClassNames(`
          font-medium resize-none
          ${inputStyles}
          ${textareaSizes[size]}
          ${textareaVariants[variant]}
          ${shape === 'pill' ? inputShapes.pill : inputShapes[shape][size]}
          ${className}
        `)}
        ref={ref}
        {...rest}
      />
    );
  }
);
